import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import "./DataSourceDonut.css";

// Register chart components
ChartJS.register(ArcElement, Tooltip, Legend);

/* The centre value is passed in through chart options rather than closed over,
   so the plugin stays a module-level singleton — registering a new plugin object
   on every render makes Chart.js rebuild its plugin cache each time. */
const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart) {
    const { width, height, ctx } = chart;
    ctx.save();

    const centerX = width / 2;
    const centerY = height / 2;
    const value = (chart.options.plugins.centerText && chart.options.plugins.centerText.value) || "100%";

    // "Total" label
    ctx.font = "400 13px Inter, sans-serif";
    ctx.fillStyle = "#94a3b8";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Total", centerX, centerY - 14);

    ctx.font = "700 24px Inter, sans-serif";
    ctx.fillStyle = "#1e293b";
    ctx.fillText(String(value), centerX, centerY + 14);

    ctx.restore();
  },
};

export default function DataSourceDonut({ dataSourceData = [], loading = false, error = null }) {
  const total = dataSourceData.reduce((sum, d) => sum + d.value, 0);
  /* Resource counts, when the caller supplies them. The centre used to read a
     hardcoded "Total 100%", which says nothing — with real data the total number
     of resources is the useful figure, and the percentage stays as the fallback
     for any caller still passing percentages alone. */
  const totalCount = dataSourceData.reduce((sum, d) => sum + (d.count || 0), 0);
  const centerValue = totalCount > 0 ? totalCount : "100%";
  /* `value` is a percentage, so the legend can print it directly, but the
     tooltip derives its own from the total — the two only agree while the values
     happen to sum to 100. They do now (homeApi's donutData sends percentages),
     but the arcs and the tooltip work off any scale, so `count` is preferred for
     the tooltip when it is there: an exact "REST API: 21 (45%)" beats a rounded
     percentage of a rounded percentage. */
  const chartData = {
    labels: dataSourceData.map((d) => d.name),
    datasets: [
      {
        data: dataSourceData.map((d) => d.value),
        backgroundColor: dataSourceData.map((d) => d.color),
        borderWidth: 0, // removes the white segment borders
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "63%",
    plugins: {
      legend: { display: false },
      centerText: { value: centerValue },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.parsed;
            const pct = total > 0 ? Math.round((val / total) * 100) : 0;
            const count = dataSourceData[context.dataIndex] && dataSourceData[context.dataIndex].count;
            return count === undefined ? ` ${context.label}: ${pct}%` : ` ${context.label}: ${count} (${pct}%)`;
          },
        },
      },
    },
  };

  /* Chart.js draws nothing at all for an empty dataset, so without this the card
     is a blank 200px gap while the request is out, and again for an org with no
     resources. "Loading" has to be distinguishable from "none" — during the
     fetch the data is empty for a different reason. */
  if (loading || error || dataSourceData.length === 0) {
    return (
      <div className="donut-container">
        <div className="donut-empty">
          {loading ? "Loading…" : error ? "Could not load data source usage." : "No data sources yet"}
        </div>
      </div>
    );
  }

  return (
    <div className="donut-container">
      {/* Height comes from .donut-ring now rather than a fixed 200px inline, so
          the ring can centre itself in whatever the card has spare. Chart.js
          still needs a sized parent — `maintainAspectRatio: false` means it
          fills this box. */}
      <div className="donut-ring">
        <Doughnut data={chartData} options={chartOptions} plugins={[centerTextPlugin]} />
      </div>
      <ul className="custom-legend">
        {dataSourceData.map((item, index) => (
          <li key={index}>
            <span className="legend-color" style={{ backgroundColor: item.color }} />
            <span className="legend-name">{item.name}</span>
            <span className="legend-pct">{item.value}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
